import { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { FileSpreadsheet, Download, Loader2, FileText, Receipt, CalendarDays, TrendingUp } from "lucide-react";

const inr = (n) => `₹ ${Number(n || 0).toLocaleString("en-IN")}`;

function MonthlySummary() {
  const now = new Date();
  const defMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  const [month, setMonth] = useState(defMonth);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const res = await api.get(`/reports/monthly-summary?month=${month}`);
      setData(res.data);
    } catch {
      toast.error("Failed to load summary");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white border border-slate-100 rounded-xl p-6 max-w-3xl mt-6" data-testid="monthly-summary-section">
      <div className="flex items-center gap-2 text-slate-800 font-medium mb-4"><TrendingUp size={18} /> Monthly Business Summary</div>
      <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
        <div>
          <label className="text-xs font-semibold text-slate-600 mb-1 block">Month</label>
          <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-44" data-testid="summary-month-input" />
        </div>
        <button onClick={load} disabled={busy || !month} data-testid="view-summary-btn"
          className="btn-primary px-5 py-2.5 rounded-lg text-sm font-medium inline-flex items-center justify-center gap-2 disabled:opacity-60">
          {busy ? <Loader2 size={16} className="animate-spin" /> : <CalendarDays size={16} />}
          View Summary
        </button>
      </div>

      {data && (
        <div className="mt-6" data-testid="summary-results">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {[
              ["Consignments", data.consignment_count],
              ["Total Freight", inr(data.total_freight)],
              ["Bills Raised", data.bill_count],
              ["Total Billed", inr(data.total_billed)],
            ].map(([label, val]) => (
              <div key={label} className="bg-slate-50 rounded-lg p-4">
                <div className="text-xs text-slate-500">{label}</div>
                <div className="text-lg font-semibold text-slate-900 mt-1">{val}</div>
              </div>
            ))}
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            {Object.entries(data.status_breakup || {}).map(([s, n]) => (
              <span key={s} className="text-xs font-medium bg-slate-100 text-slate-600 rounded-full px-3 py-1">{s}: {n}</span>
            ))}
          </div>

          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <div className="text-sm font-semibold text-slate-700 mb-2">Top Parties</div>
              {data.top_parties.length === 0 ? <div className="text-xs text-slate-400">No data</div> : (
                <table className="w-full text-sm">
                  <thead><tr className="text-left text-xs text-slate-500"><th className="py-1">Party</th><th className="py-1 text-right">CNs</th><th className="py-1 text-right">Freight</th></tr></thead>
                  <tbody>
                    {data.top_parties.map((p) => (
                      <tr key={p.name} className="border-t border-slate-100">
                        <td className="py-1.5 truncate max-w-[140px]" title={p.name}>{p.name}</td>
                        <td className="py-1.5 text-right">{p.cns}</td>
                        <td className="py-1.5 text-right">{inr(p.freight)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div>
              <div className="text-sm font-semibold text-slate-700 mb-2">Busiest Trucks</div>
              {data.busiest_trucks.length === 0 ? <div className="text-xs text-slate-400">No data</div> : (
                <table className="w-full text-sm">
                  <thead><tr className="text-left text-xs text-slate-500"><th className="py-1">Truck</th><th className="py-1 text-right">Trips</th><th className="py-1 text-right">Freight</th></tr></thead>
                  <tbody>
                    {data.busiest_trucks.map((t) => (
                      <tr key={t.truck_no} className="border-t border-slate-100">
                        <td className="py-1.5">{t.truck_no}</td>
                        <td className="py-1.5 text-right">{t.trips}</td>
                        <td className="py-1.5 text-right">{inr(t.freight)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function Reports() {
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [party, setParty] = useState("");
  const [truck, setTruck] = useState("");
  const [busy, setBusy] = useState("");

  const download = async (kind) => {
    setBusy(kind);
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.set("date_from", dateFrom);
      if (dateTo) params.set("date_to", dateTo);
      if (party) params.set("party", party);
      if (kind === "consignments" && truck) params.set("truck", truck);
      const res = await api.get(`/reports/${kind}?${params.toString()}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${kind}_report.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Excel report downloaded");
    } catch {
      toast.error("Failed to generate report");
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="p-8" data-testid="reports-page">
      <div className="mb-6">
        <h1 className="font-display text-3xl text-slate-900">Excel Reports</h1>
        <p className="text-slate-500 text-sm">Download consignment &amp; bill reports filtered by date, party or truck</p>
      </div>

      <div className="bg-white border border-slate-100 rounded-xl p-6 max-w-3xl">
        <div className="flex items-center gap-2 text-slate-800 font-medium mb-4"><FileSpreadsheet size={18} /> Filters</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-semibold text-slate-600 mb-1 block">From Date</label>
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="report-date-from" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600 mb-1 block">To Date</label>
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="report-date-to" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600 mb-1 block">Party Name (contains)</label>
            <Input value={party} onChange={(e) => setParty(e.target.value)} placeholder="e.g. MULICK" data-testid="report-party-input" />
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-600 mb-1 block">Truck No (contains, consignments only)</label>
            <Input value={truck} onChange={(e) => setTruck(e.target.value)} placeholder="e.g. WB33" data-testid="report-truck-input" />
          </div>
        </div>

        <div className="mt-6 flex flex-col sm:flex-row gap-3">
          <button onClick={() => download("consignments")} disabled={!!busy} data-testid="download-consignments-report-btn"
            className="btn-primary px-5 py-2.5 rounded-lg text-sm font-medium inline-flex items-center justify-center gap-2 disabled:opacity-60">
            {busy === "consignments" ? <Loader2 size={16} className="animate-spin" /> : <FileText size={16} />}
            Consignments Report (.xlsx)
          </button>
          <button onClick={() => download("bills")} disabled={!!busy} data-testid="download-bills-report-btn"
            className="px-5 py-2.5 rounded-lg text-sm font-medium inline-flex items-center justify-center gap-2 border border-slate-200 bg-white text-slate-800 hover:bg-slate-50 disabled:opacity-60">
            {busy === "bills" ? <Loader2 size={16} className="animate-spin" /> : <Receipt size={16} />}
            Bills Report (.xlsx)
          </button>
        </div>
        <div className="mt-4 text-xs text-slate-400 flex items-center gap-1.5">
          <Download size={13} /> Leave filters empty to export everything.
        </div>
      </div>

      <MonthlySummary />
    </div>
  );
}
